import { useTranslation } from "react-i18next";
import { Route as RouteIcon } from "lucide-react";
import { useNavigate } from "react-router";
import { Badge } from "@/components/ui/Badge";
import type { BadgeVariant } from "@/components/ui/Badge";
import { EmptyState } from "@/components/ui/EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";
import { cn } from "@/lib/cn";
import { routeDeviationSec } from "@/features/dashboard/lib/panelData";
import type { Route, RouteStatus } from "@/types/routes";

interface Props {
  routes: Route[];
  isLoading: boolean;
  /** When provided, the empty-state "Create route" button calls this instead of navigating to /routes. */
  onCreateRoute?: () => void;
}

/**
 * Maps a route backend status to a Badge variant.
 * Route statuses: planned | active | completed | archived
 * Badge variants: draft | planned | in_progress | completed | failed | pending
 */
function statusToBadgeVariant(status: RouteStatus): BadgeVariant {
  const map: Record<RouteStatus, BadgeVariant> = {
    planned: "planned",
    active: "in_progress",
    completed: "completed",
    archived: "draft",
    cancelled: "draft",
  };
  return map[status];
}

const SKELETON_ROWS = 5;
const EM_DASH = "—";

/** Progress share of a route, 0–100, or null when the stop count is unusable. */
function progressPercent(route: Route): number | null {
  const total = route.stops_count;
  if (!Number.isFinite(total) || total <= 0) return null;
  const done = route.stops_count_actuals_done;
  if (done == null || !Number.isFinite(done)) return null;
  return Math.max(0, Math.min(100, (done / total) * 100));
}

/**
 * Today's routes — the mockup's six columns: Маршрут / Статус / ТС и водитель /
 * Прогресс / Отклонение / Пробег.
 *
 * Replaces the five-column version in place (same path, same export). Two rule
 * violations it used to carry are deliberately NOT inherited:
 *
 *  1. `style={{ backgroundColor: route.color }}` — an inline style (§10.6). The
 *     route colour is genuinely tenant DATA and has no token, so it now rides a
 *     CSS custom property consumed by an arbitrary-value utility: the value
 *     stays runtime, the styling stays in the class system.
 *  2. `bg-amber-50/40` / `hover:bg-blue-50/40` — literal palette tints (§10.10)
 *     used to mark the ACTIVE row, i.e. a selected/emphasised state painted in
 *     a fixed colour. Now a brand token wash, so it follows the dealer palette.
 */
export function TodayRoutesTable({ routes, isLoading, onCreateRoute }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
      {/* The TABLE scrolls, never the page: six columns do not fit a phone, and
          a body-level horizontal scrollbar breaks every other section too. */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/60">
              <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-widest text-gray-400">
                {t("dashboard.table.route")}
              </th>
              <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-widest text-gray-400">
                {t("dashboard.table.status")}
              </th>
              <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-widest text-gray-400">
                {t("dashboard.table.vehicleAndDriver")}
              </th>
              <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-widest text-gray-400">
                {t("dashboard.table.progress")}
              </th>
              <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-widest text-gray-400">
                {t("dashboard.table.deviation")}
              </th>
              <th className="px-5 py-3.5 text-right text-xs font-semibold uppercase tracking-widest text-gray-400">
                {t("dashboard.table.distance")}
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading
              ? Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-50 last:border-0">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-2.5 w-2.5 rounded-full" />
                        <Skeleton className="h-4 w-32" />
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <Skeleton className="h-5 w-20 rounded-full" />
                    </td>
                    <td className="px-5 py-4">
                      <Skeleton className="h-4 w-24" />
                    </td>
                    <td className="px-5 py-4">
                      <Skeleton className="h-4 w-28" />
                    </td>
                    <td className="px-5 py-4">
                      <Skeleton className="h-4 w-16" />
                    </td>
                    <td className="px-5 py-4 text-right">
                      <Skeleton className="ml-auto h-4 w-16" />
                    </td>
                  </tr>
                ))
              : routes.map((route) => (
                  <TodayRouteRow
                    key={route.id}
                    route={route}
                    onOpen={() => navigate(`/routes/${route.id}`)}
                  />
                ))}
          </tbody>
        </table>
      </div>

      {/* Empty state */}
      {!isLoading && routes.length === 0 && (
        <EmptyState
          icon={RouteIcon}
          title={t("dashboard.noRoutesToday")}
          subtitle={t("dashboard.table.routesWillAppear")}
          action={
            onCreateRoute ? (
              <button
                type="button"
                onClick={onCreateRoute}
                className="rounded-xl bg-brand-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-primary-hover"
              >
                {t("dashboard.createRoute")}
              </button>
            ) : undefined
          }
        />
      )}
    </div>
  );
}

function TodayRouteRow({
  route,
  onOpen,
}: {
  route: Route;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const pct = progressPercent(route);
  const deviation = routeDeviationSec(route);
  const driver = route.resolved_driver ?? route.driver ?? null;
  const driverName = driver
    ? `${driver.first_name} ${driver.last_name}`.trim()
    : null;
  const distance =
    route.planned_mileage_km != null ? Number(route.planned_mileage_km) : null;
  const isDraft = route.status === "planned" && route.released_at == null;

  return (
    <tr
      onClick={onOpen}
      className={cn(
        "group cursor-pointer border-b border-gray-50 transition-colors last:border-0",
        // Brand-token washes, not the former `bg-amber-50/40` / `blue-50/40`
        // literals: an emphasised row must follow the dealer's palette.
        route.status === "active"
          ? "bg-brand-primary/5 hover:bg-brand-primary/10"
          : "hover:bg-gray-50",
      )}
    >
      <td className="px-5 py-4">
        <div className="flex items-center gap-2.5">
          {route.color ? (
            // The route colour is tenant DATA with no token to map it to, so
            // the runtime value rides a CSS custom property that an arbitrary
            // -value utility reads. This is the ONE legitimate runtime colour
            // on the panel; it is not a palette choice (§10.10) but a value the
            // dispatcher themselves assigned to this route.
            <span
              data-testid={`route-color-${route.id}`}
              className="h-2.5 w-2.5 shrink-0 rounded-full bg-[var(--route-color)] ring-2 ring-white"
              style={{ "--route-color": route.color } as React.CSSProperties}
            />
          ) : (
            <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-gray-200 ring-2 ring-white" />
          )}
          <span className="font-semibold text-gray-900 transition-colors group-hover:text-brand-primary">
            {route.name}
          </span>
        </div>
      </td>

      <td className="px-5 py-4">
        {/* DYNAMIC i18n KEY (`routes.status.${route.status}`), invisible to
            `check:i18n` (DEV_RULES §10.9). The five keys, all pre-existing:
            routes.status.planned / .active / .completed / .archived /
            .cancelled — plus routes.chip.draft for an unreleased planned
            route. Pinned by a rendered-text test. */}
        <Badge
          variant={statusToBadgeVariant(route.status)}
          label={
            isDraft
              ? t("routes.chip.draft")
              : t(`routes.status.${route.status}`)
          }
        />
      </td>

      <td className="px-5 py-4">
        <p className="text-gray-700">
          {route.vehicle?.name ?? (
            <span className="text-gray-300">{EM_DASH}</span>
          )}
        </p>
        {driverName ? (
          <p className="text-xs text-gray-400">{driverName}</p>
        ) : (
          <p className="text-xs text-brand-primary">
            {t("dashboard.table.noDriver")}
          </p>
        )}
      </td>

      <td className="px-5 py-4">
        {pct == null ? (
          <span className="text-gray-300">{EM_DASH}</span>
        ) : (
          <div className="min-w-[8rem]">
            <p className="mb-1 text-xs text-gray-500">
              {t("dashboard.table.stopsProgress", {
                done: route.stops_count_actuals_done ?? 0,
                total: route.stops_count,
                // BUG-719: plural forms agree with the total («из 21 точки»).
                count: route.stops_count,
              })}
            </p>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
              {/* Computed share → runtime width (project pattern); colour
                  stays a token and encodes MEANING: complete is a success,
                  anything still running is ordinary brand progress. */}
              <div
                data-testid={`route-progress-${route.id}`}
                data-pct={String(Math.round(pct))}
                className={cn(
                  "h-full rounded-full transition-all",
                  pct >= 100 ? "bg-brand-success" : "bg-brand-primary",
                )}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}
      </td>

      <td className="px-5 py-4">
        <DeviationCell deviationSec={deviation} isDraft={isDraft} />
      </td>

      <td className="px-5 py-4 text-right text-sm">
        {distance != null && Number.isFinite(distance) ? (
          <span className="font-medium text-gray-600">
            {distance} {t("units.km")}
          </span>
        ) : (
          <span className="text-gray-300">{EM_DASH}</span>
        )}
      </td>
    </tr>
  );
}

/**
 * The «Отклонение» cell.
 *
 * `null` is NOT «по плану». A route with no measured deviation renders an
 * em-dash, because claiming punctuality nobody measured is the same class of
 * lie as rendering a failed request as a zero. Today that is every route — the
 * backend field does not exist yet (see `routeDeviationSec`) — so the column
 * reads «—» until it lands, which is the honest degradation.
 */
function DeviationCell({
  deviationSec,
  isDraft,
}: {
  deviationSec: number | null;
  isDraft: boolean;
}) {
  const { t } = useTranslation();

  if (isDraft) {
    return (
      <span className="text-xs text-gray-400">
        {t("dashboard.table.notSent")}
      </span>
    );
  }
  if (deviationSec == null) {
    return <span className="text-gray-300">{EM_DASH}</span>;
  }

  // The ±300 s dead band is the backend's own `ON_TIME_TOLERANCE_SEC`
  // (`public_api/services_eta.py`), mirrored here only to choose a label for an
  // already-computed number. The VERDICT should come from the server's
  // `schedule_status` once it ships — see the backend TODO in `panelData.ts`.
  const minutes = Math.round(deviationSec / 60);
  if (Math.abs(deviationSec) <= 300) {
    return (
      <span className="text-xs font-medium text-brand-success">
        {t("dashboard.table.onSchedule")}
      </span>
    );
  }
  const late = deviationSec > 0;
  return (
    <span
      className={cn(
        "text-xs font-semibold",
        late ? "text-brand-danger" : "text-gray-500",
      )}
    >
      {t(
        late
          ? "dashboard.table.deviationLate"
          : "dashboard.table.deviationEarly",
        {
          minutes: Math.abs(minutes),
        },
      )}
    </span>
  );
}
